import { t } from '@lingui/macro'
import { CandidateIndexDTO } from '@om1/aspen-api/codegen/client/models/CandidateIndexDTO'
import { Popconfirm, Tooltip } from 'antd'
import { CalendarClock, CalendarX, UserX } from 'lucide-react'
import { useState } from 'react'
import { useAppointmentActions } from '../hooks/useAppointmentActions'
import { useUserCanAccess } from '../hooks/useUserPermissions'
import { AspenPermissions } from '../utils'
import { canActOnAppointment } from '../utils/appointment'
import RescheduleAppointmentModal from './RescheduleAppointmentModal'

/**
 * Per-row coordinator actions on a candidate's appointment (APP-1221, D9).
 *
 * ## Permission
 *
 * Hidden entirely without `manage:candidates`, rather than shown-and-disabled.
 * A disabled control invites someone to ask why they cannot use it; an absent
 * one simply is not their job. The API enforces the same scope regardless — this
 * is presentation, not the guard.
 *
 * ## When nothing is offered
 *
 * Actions appear only for an appointment that can still be acted on upstream:
 * `held` or `confirmed`, and carrying a Cal.com booking uid. A cancelled or
 * completed booking cannot be cancelled again, Cal.com refuses no-show on
 * something that has not happened, and a `legacy_preference` row has nothing
 * upstream at all — so offering the buttons would only produce errors the
 * coordinator can do nothing about.
 */

/**
 * The fields these actions actually read, rather than a whole DTO.
 *
 * `CandidateIndexDTO` (the list) and `CandidateDTO` (the details page) carry the
 * same shape for all of them, and typing the subset lets one component serve
 * both without a cast that would hide a future divergence.
 */
export type AppointmentActionsCandidate = Pick<CandidateIndexDTO, 'id' | 'firstName' | 'lastName' | 'timezone' | 'languagePreference' | 'appointment'>

export interface AppointmentActionsProps {
    candidate: AppointmentActionsCandidate
    /** Study slug — the Auth0 organization name the coordinator is scoped to. */
    study: string
    /** Called after a successful action so the list can reflect the new state. */
    onChanged?: () => void
}

export const AppointmentActions = ({ candidate, study, onChanged }: AppointmentActionsProps) => {
    const { canAccess } = useUserCanAccess([AspenPermissions.MANAGE_CANDIDATES])
    const { cancelAppointment, rescheduleAppointment, markNoShow, isSubmitting } = useAppointmentActions()
    const [rescheduleOpen, setRescheduleOpen] = useState(false)

    const appointment = candidate.appointment
    if (!canAccess || !canActOnAppointment(appointment) || !appointment) {
        return null
    }

    const name = `${candidate.firstName} ${candidate.lastName}`.trim()

    const afterAction = (ok: boolean) => {
        // Refetch on success only. Cal.com's webhook is what moves the status,
        // so the row may briefly still show the old one — that is honest, and
        // better than optimistically rendering a state we have not confirmed.
        if (ok) onChanged?.()
    }

    return (
        <div className='flex items-center gap-2'>
            <Tooltip title={t`Reschedule`}>
                <button
                    type='button'
                    aria-label={t`Reschedule appointment for ${name}`}
                    disabled={isSubmitting}
                    onClick={() => setRescheduleOpen(true)}
                    className='text-gray-600 hover:text-blue-600 disabled:opacity-40'
                >
                    <CalendarClock size={18} />
                </button>
            </Tooltip>

            <Popconfirm
                title={t`Cancel this appointment?`}
                description={t`The slot is released for other patients and the patient is notified by Cal.com.`}
                okText={t`Cancel appointment`}
                cancelText={t`Keep it`}
                okButtonProps={{ danger: true, loading: isSubmitting }}
                onConfirm={async () => afterAction(await cancelAppointment(candidate.id))}
            >
                <Tooltip title={t`Cancel appointment`}>
                    <button
                        type='button'
                        aria-label={t`Cancel appointment for ${name}`}
                        disabled={isSubmitting}
                        className='text-gray-600 hover:text-red-600 disabled:opacity-40'
                    >
                        <CalendarX size={18} />
                    </button>
                </Tooltip>
            </Popconfirm>

            <Popconfirm
                title={t`Mark the patient as a no-show?`}
                description={t`Records the absence in Cal.com. The status here updates once Cal.com confirms it.`}
                okText={t`Mark no-show`}
                cancelText={t`Cancel`}
                okButtonProps={{ loading: isSubmitting }}
                onConfirm={async () => afterAction(await markNoShow(candidate.id))}
            >
                <Tooltip title={t`Mark no-show`}>
                    <button
                        type='button'
                        aria-label={t`Mark no-show for ${name}`}
                        disabled={isSubmitting}
                        className='text-gray-600 hover:text-orange-600 disabled:opacity-40'
                    >
                        <UserX size={18} />
                    </button>
                </Tooltip>
            </Popconfirm>

            <RescheduleAppointmentModal
                open={rescheduleOpen}
                candidateId={candidate.id}
                candidateName={name}
                appointment={appointment}
                candidateTimezone={candidate.timezone}
                study={study}
                language={candidate.languagePreference}
                isSubmitting={isSubmitting}
                onClose={() => setRescheduleOpen(false)}
                onConfirm={async (slotStartIso, reason) => {
                    const ok = await rescheduleAppointment(candidate.id, slotStartIso, reason)
                    afterAction(ok)
                    return ok
                }}
            />
        </div>
    )
}

export default AppointmentActions
